'use client';

import { useMotionValue, useSpring, type MotionValue } from 'motion/react';
import { useEffect, useRef, type RefObject } from 'react';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';

const PULL = 0.18;
const MAX_OFFSET = 6; // pixels — keep subtle per docs/design-principles.md §6
const SPRING = { stiffness: 300, damping: 25 };

/**
 * Cursor-magnetic motion values. Attach `ref` to a block element; bind
 * the returned `x`/`y` to a Motion `style.x`/`style.y` to make it pull
 * toward the cursor. Reduced-motion: returns motion values that never move.
 */
export function useMagnetic<T extends HTMLElement>(): {
  ref: RefObject<T | null>;
  x: MotionValue<number>;
  y: MotionValue<number>;
} {
  const ref = useRef<T | null>(null);
  const xMv = useMotionValue(0);
  const yMv = useMotionValue(0);
  const x = useSpring(xMv, SPRING);
  const y = useSpring(yMv, SPRING);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * PULL;
      const dy = (e.clientY - cy) * PULL;
      xMv.set(Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, dx)));
      yMv.set(Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, dy)));
    };
    const onLeave = () => {
      xMv.set(0);
      yMv.set(0);
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [reduced, xMv, yMv]);

  return { ref, x, y };
}
